
import React, { useState, useContext, useEffect, useRef } from 'react';
import type { User, CustomLLMProvider } from '../types';
import type { GeminiKeyEntry } from '../services/authService';
import Modal from './Modal';
import { Loader2Icon, TrashIcon, ShieldCheckIcon, ClipboardCopyIcon, SparklesIcon, BrainCircuitIcon, HistoryIcon, UserIcon, ChipIcon, SlidersIcon } from '../constants';
import { ModelSelector } from './ModelSelector';
import SyncTokens from './SyncTokens';
import { ToastContext } from '../App';

import { DEFAULT_GENERATOR_PROMPT, DEFAULT_OPTIMIZER_PROMPT, DEFAULT_TEST_SYSTEM_PROMPT, GEMINI_MODELS } from '../services/geminiService';
import QRCode from 'qrcode';


interface AccountSettingsModalProps {
    user: User;
    onClose: () => void;
    onChangePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message: string; }>;
    onDeleteAccount: (password: string) => Promise<{ success: boolean; message: string; }>;
    onGetUserDetails: () => Promise<{ twoFactorEnabled: boolean } | null>;
    onGenerate2FA: () => Promise<{ secret: string; otpauthUrl: string; } | null>;
    onEnable2FA: (token: string) => Promise<{ success: boolean; message: string; }>;
    onDisable2FA: (token: string) => Promise<{ success: boolean; message: string; }>;
    onSaveGeminiKey: (apiKey: string, label?: string) => Promise<{ success: boolean; message: string; }>;
    onRemoveGeminiKey: (keyId?: number) => Promise<{ success: boolean; message: string; }>;
    onTestGeminiKey: (apiKey?: string) => Promise<{ success: boolean; message: string; }>;
    onGetGeminiKeys: () => Promise<{ hasKey: boolean; keyCount: number; keys: GeminiKeyEntry[]; }>; 
    onUpdateUser: (updates: Partial<User>) => void;
    settings?: { 
        globalSystemInstruction: string;
        promptGeneratorSystemPrompt?: string;
        promptOptimizerSystemPrompt?: string;
        selectedProviderId?: string;
        testModel?: string;
        generatorModel?: string;
        optimizerModel?: string;
        customProviders?: CustomLLMProvider[];
        enableGeminiKeyRotation?: boolean;
        customGeminiModels?: string[];
    };
    onUpdateSettings: (settings: { 
        globalSystemInstruction: string;
        promptGeneratorSystemPrompt?: string;
        promptOptimizerSystemPrompt?: string;
        selectedProviderId?: string;
        testModel?: string;
        generatorModel?: string;
        optimizerModel?: string;
        customProviders?: CustomLLMProvider[];
        enableGeminiKeyRotation?: boolean;
        customGeminiModels?: string[];
    }) => void;
    isMobile?: boolean;
    isTouch?: boolean;
}

const AccountSettingsModal: React.FC<AccountSettingsModalProps> = (props) => {
    const { user, onClose, onChangePassword, onDeleteAccount, onGetUserDetails, onGenerate2FA, onEnable2FA, onDisable2FA, onSaveGeminiKey, onRemoveGeminiKey, onTestGeminiKey, onGetGeminiKeys, onUpdateUser, settings, onUpdateSettings } = props;
    const { showToast } = useContext(ToastContext);
    const [isLoading, setIsLoading] = useState(false);
    
    // Password Change State
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    
    // Delete Account State
    const [deletePassword, setDeletePassword] = useState('');
    const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);

    // 2FA State
    const [is2FAEnabled, setIs2FAEnabled] = useState(false);
    const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'gemini' | 'workspace'>('profile');
    
    // Workspace State
    const [globalInstruction, setGlobalInstruction] = useState(settings?.globalSystemInstruction || '');
    const [generatorSystemPrompt, setGeneratorSystemPrompt] = useState(settings?.promptGeneratorSystemPrompt || '');
    const [optimizerSystemPrompt, setOptimizerSystemPrompt] = useState(settings?.promptOptimizerSystemPrompt || '');
    
    // AI Provider State
    const [selectedProviderId, setSelectedProviderId] = useState(settings?.selectedProviderId || 'gemini');
    const [customProviders, setCustomProviders] = useState<CustomLLMProvider[]>(settings?.customProviders || []);
    const [testModel, setTestModel] = useState(settings?.testModel || '');
    const [generatorModel, setGeneratorModel] = useState(settings?.generatorModel || '');
    const [optimizerModel, setOptimizerModel] = useState(settings?.optimizerModel || '');

    // Custom Provider Form State
    const [newProviderName, setNewProviderName] = useState('');
    const [newProviderUrl, setNewProviderUrl] = useState('');
    const [newProviderKey, setNewProviderKey] = useState('');
    const [newProviderModel, setNewProviderModel] = useState('');
    const [newProviderAvailableModels, setNewProviderAvailableModels] = useState('');
    
    // Custom Gemini Models State
    const [customGeminiModels, setCustomGeminiModels] = useState<string[]>(settings?.customGeminiModels || []);
    const [newCustomGeminiModel, setNewCustomGeminiModel] = useState('');

    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const [twoFactorView, setTwoFactorView] = useState<'idle' | 'setup' | 'disable'>('idle');
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const qrCanvasRef = useRef<HTMLCanvasElement>(null);

    const [enableGeminiKeyRotation, setEnableGeminiKeyRotation] = useState(settings?.enableGeminiKeyRotation ?? true);

    // Gemini API Key State
    const [hasGeminiKey, setHasGeminiKey] = useState(user.hasGeminiApiKey);
    const [geminiKeyInput, setGeminiKeyInput] = useState('');
    const [geminiKeyLabelInput, setGeminiKeyLabelInput] = useState('');
    const [geminiKeys, setGeminiKeys] = useState<GeminiKeyEntry[]>([]);
    const [isSavingGeminiKey, setIsSavingGeminiKey] = useState(false);
    const [isTestingGeminiKey, setIsTestingGeminiKey] = useState(false);

    const loadGeminiKeys = async () => {
        const keyState = await onGetGeminiKeys();
        setHasGeminiKey(keyState.hasKey);
        setGeminiKeys(keyState.keys);
        onUpdateUser({ hasGeminiApiKey: keyState.hasKey, geminiKeyCount: keyState.keyCount });
        return keyState;
    };

    useEffect(() => {
        loadGeminiKeys();
    }, []);

    useEffect(() => {
        onGetUserDetails().then(userDetails => {
            if (userDetails) {
                setIs2FAEnabled(userDetails.twoFactorEnabled);
            }
        });
    }, [onGetUserDetails]);

    useEffect(() => {
        if (qrCodeUrl && qrCanvasRef.current) {
            QRCode.toCanvas(qrCanvasRef.current, qrCodeUrl, { width: 200, margin: 1 }, (error) => {
                if (error) console.error(error);
            });
        }
    }, [qrCodeUrl, twoFactorView, activeTab]);

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmNewPassword) { showToast('Die neuen Passwörter stimmen nicht überein.', 'error'); return; }
        if (!currentPassword || !newPassword) { showToast('Bitte füllen Sie alle Felder aus.', 'error'); return; }
        setIsLoading(true);
        const result = await onChangePassword(currentPassword, newPassword);
        setIsLoading(false);
        showToast(result.message, result.success ? 'success' : 'error');
        if (result.success) { setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword(''); }
    };

    const handleSaveGeminiApiKey = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!geminiKeyInput.trim()) {
            showToast('Bitte gib einen gültigen Gemini API-Schlüssel ein.', 'error');
            return;
        }
        setIsSavingGeminiKey(true);
        const sanitizedKey = geminiKeyInput.trim();
        const sanitizedLabel = geminiKeyLabelInput.trim();
        const result = await onSaveGeminiKey(sanitizedKey, sanitizedLabel || undefined);
        setIsSavingGeminiKey(false);
        showToast(result.message, result.success ? 'success' : 'error');
        if (result.success) {
            setGeminiKeyInput('');
            setGeminiKeyLabelInput('');
            await loadGeminiKeys();
        }
    };

    const handleRemoveGeminiApiKey = async (keyId?: number) => {
        const result = await onRemoveGeminiKey(keyId);
        showToast(result.message, result.success ? 'success' : 'error');
        if (result.success) {
            setGeminiKeyInput('');
            await loadGeminiKeys();
        }
    };

    const handleTestGeminiApiKey = async () => {
        const keyToTest = geminiKeyInput.trim();
        if (!keyToTest && !hasGeminiKey) {
            showToast('Bitte gib einen Schlüssel ein oder speichere zuerst deinen persönlichen Gemini API-Schlüssel.', 'error');
            return;
        }
        setIsTestingGeminiKey(true);
        const result = await onTestGeminiKey(keyToTest || undefined);
        setIsTestingGeminiKey(false);
        showToast(result.message, result.success ? 'success' : 'error');
    };

    const handleDeleteAccountSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!deletePassword) { showToast('Bitte geben Sie Ihr Passwort ein, um die Löschung zu bestätigen.', 'error'); return; }
        setIsLoading(true);
        const result = await onDeleteAccount(deletePassword);
        if (!result.success) { setIsLoading(false); showToast(result.message, 'error'); } 
        else { showToast(result.message); }
    };

    const handleStart2FASetup = async () => {
        setIsLoading(true);
        const result = await onGenerate2FA();
        if (result) {
            setSecretKey(result.secret);
            setQrCodeUrl(result.otpauthUrl);
            setTwoFactorView('setup');
        } else {
            showToast('Fehler beim Generieren des 2FA-Geheimnisses.', 'error');
        }
        setIsLoading(false);
    };

    const handleEnable2FA = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const result = await onEnable2FA(verificationCode);
            showToast(result.message, result.success ? 'success' : 'error');
            if (result.success) {
                setIs2FAEnabled(true);
                setTwoFactorView('idle');
                setVerificationCode('');
            }
        } catch (err) {
            showToast('Verbindung zum Server fehlgeschlagen.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDisable2FA = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const result = await onDisable2FA(verificationCode);
            showToast(result.message, result.success ? 'success' : 'error');
            if (result.success) {
                setIs2FAEnabled(false);
                setTwoFactorView('idle');
                setVerificationCode('');
            }
        } catch (err) {
            showToast('Verbindung zum Server fehlgeschlagen.', 'error');
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleSaveWorkspaceSettings = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSavingSettings(true);
        try {
            onUpdateSettings({ 
                globalSystemInstruction: globalInstruction,
                promptGeneratorSystemPrompt: generatorSystemPrompt,
                promptOptimizerSystemPrompt: optimizerSystemPrompt,
                selectedProviderId,
                testModel,
                generatorModel,
                optimizerModel,
                customProviders,
                enableGeminiKeyRotation,
                customGeminiModels
            });
            showToast('Workspace-Einstellungen gespeichert!');
        } catch (err) {
            showToast('Fehler beim Speichern der Einstellungen.', 'error');
        } finally {
            setIsSavingSettings(false);
        }
    };
    
    const handleAddCustomProvider = () => {
        if (!newProviderName || !newProviderUrl || !newProviderModel) {
            showToast('Bitte Name, Base URL und Standard-Modell ausfüllen.', 'error');
            return;
        }
        const modelsArray = newProviderAvailableModels
            .split(',')
            .map(m => m.trim())
            .filter(m => m.length > 0);
        const newProvider: CustomLLMProvider = {
            id: 'custom-' + Date.now().toString(),
            name: newProviderName,
            baseUrl: newProviderUrl,
            apiKey: newProviderKey,
            defaultModel: newProviderModel,
            availableModels: modelsArray.length ? modelsArray : undefined,
        };
        const updatedProviders = [...customProviders, newProvider];
        setCustomProviders(updatedProviders);
        setNewProviderName(''); setNewProviderUrl(''); setNewProviderKey(''); setNewProviderModel(''); setNewProviderAvailableModels('');
        
        onUpdateSettings({ 
            globalSystemInstruction: globalInstruction,
            promptGeneratorSystemPrompt: generatorSystemPrompt,
            promptOptimizerSystemPrompt: optimizerSystemPrompt,
            selectedProviderId,
            testModel,
            generatorModel,
            optimizerModel,
            customProviders: updatedProviders,
            enableGeminiKeyRotation,
            customGeminiModels
        });
        showToast('Provider hinzugefügt und Einstellungen gespeichert!');
    };

    const handleRemoveCustomProvider = (id: string) => {
        const removedProvider = customProviders.find(p => p.id === id);
        const removedModels = new Set(
            (removedProvider?.availableModels?.length
                ? removedProvider.availableModels
                : [removedProvider?.defaultModel || '']
            ).filter(Boolean)
        );
        const updatedProviders = customProviders.filter(p => p.id !== id);
        setCustomProviders(updatedProviders);
        
        const newSelectedProviderId = selectedProviderId === id ? 'gemini' : selectedProviderId;
        if (selectedProviderId === id) setSelectedProviderId('gemini');

        const nextTestModel = (selectedProviderId === id || removedModels.has(testModel)) ? '' : testModel;
        const nextGenModel = (selectedProviderId === id || removedModels.has(generatorModel)) ? '' : generatorModel;
        const nextOptModel = (selectedProviderId === id || removedModels.has(optimizerModel)) ? '' : optimizerModel;

        if (nextTestModel !== testModel) setTestModel(nextTestModel);
        if (nextGenModel !== generatorModel) setGeneratorModel(nextGenModel);
        if (nextOptModel !== optimizerModel) setOptimizerModel(nextOptModel);
        
        onUpdateSettings({ 
            globalSystemInstruction: globalInstruction,
            promptGeneratorSystemPrompt: generatorSystemPrompt,
            promptOptimizerSystemPrompt: optimizerSystemPrompt,
            selectedProviderId: newSelectedProviderId,
            testModel: nextTestModel,
            generatorModel: nextGenModel,
            optimizerModel: nextOptModel,
            customProviders: updatedProviders,
            enableGeminiKeyRotation,
            customGeminiModels
        });
        showToast('Provider entfernt und Einstellungen gespeichert!');
    };

    const handleAddCustomGeminiModel = () => {
        const modelName = newCustomGeminiModel.trim();
        if (!modelName) {
            showToast('Bitte einen Modellnamen eingeben.', 'error');
            return;
        }
        if (GEMINI_MODELS.some(m => m.id === modelName) || customGeminiModels.includes(modelName)) {
            showToast('Dieses Modell existiert bereits.', 'error');
            return;
        }
        const updatedModels = [...customGeminiModels, modelName];
        setCustomGeminiModels(updatedModels);
        setNewCustomGeminiModel('');
        
        onUpdateSettings({ 
            globalSystemInstruction: globalInstruction,
            promptGeneratorSystemPrompt: generatorSystemPrompt,
            promptOptimizerSystemPrompt: optimizerSystemPrompt,
            selectedProviderId,
            testModel,
            generatorModel,
            optimizerModel,
            customProviders,
            enableGeminiKeyRotation,
            customGeminiModels: updatedModels
        });
        showToast('Gemini Modell hinzugefügt!');
    };

    const handleRemoveCustomGeminiModel = (modelToRemove: string) => {
        const updatedModels = customGeminiModels.filter(m => m !== modelToRemove);
        setCustomGeminiModels(updatedModels);

        const nextTestModel = testModel === modelToRemove ? '' : testModel;
        const nextGenModel = generatorModel === modelToRemove ? '' : generatorModel;
        const nextOptModel = optimizerModel === modelToRemove ? '' : optimizerModel;

        if (nextTestModel !== testModel) setTestModel(nextTestModel);
        if (nextGenModel !== generatorModel) setGeneratorModel(nextGenModel);
        if (nextOptModel !== optimizerModel) setOptimizerModel(nextOptModel);
        
        onUpdateSettings({ 
            globalSystemInstruction: globalInstruction,
            promptGeneratorSystemPrompt: generatorSystemPrompt,
            promptOptimizerSystemPrompt: optimizerSystemPrompt,
            selectedProviderId,
            testModel: nextTestModel,
            generatorModel: nextGenModel,
            optimizerModel: nextOptModel,
            customProviders,
            enableGeminiKeyRotation,
            customGeminiModels: updatedModels
        });
        showToast('Gemini Modell entfernt!');
    };

    const handleCopyToClipboard = (text: string, message: string) => {
        navigator.clipboard.writeText(text).then(() => showToast(message), () => showToast('Fehler beim Kopieren', 'error'));
    };
    
    const render2FAContent = () => {
        if (twoFactorView === 'setup') {
            return (
                <form onSubmit={handleEnable2FA} className="space-y-4 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                    <p className="text-sm font-semibold">1. Scannen Sie den QR-Code mit Ihrer Authenticator-App.</p>
                    <div className="flex justify-center my-4">
                        <canvas ref={qrCanvasRef}></canvas>
                    </div>
                    <p className="text-sm font-semibold">Oder geben Sie den Schlüssel manuell ein:</p>
                    <div className="flex items-center gap-2 p-2 rounded-md bg-gray-200 dark:bg-gray-800">
                        <code className="flex-grow text-center tracking-wider font-mono">{secretKey}</code>
                        <button type="button" onClick={() => handleCopyToClipboard(secretKey, 'Schlüssel kopiert!')} className="p-2 rounded hover:bg-gray-300 dark:hover:bg-gray-600"><ClipboardCopyIcon/></button>
                    </div>
                    <p className="text-sm font-semibold">2. Geben Sie den 6-stelligen Code aus der App ein, um die Einrichtung zu bestätigen.</p>
                    <div>
                        <label htmlFor="verificationCode" className="text-sm font-medium text-gray-600 dark:text-gray-300">Bestätigungscode</label>
                        <input type="text" name="verificationCode" id="verificationCode" required autoFocus value={verificationCode} onChange={e => setVerificationCode(e.target.value)} className="input-field" inputMode="numeric" pattern="\d{6}" maxLength={6} />
                    </div>
                    <div className="flex justify-end gap-4">
                        <button type="button" onClick={() => setTwoFactorView('idle')} disabled={isLoading} className="btn-secondary">Abbrechen</button>
                        <button type="submit" disabled={isLoading} className="btn-primary w-48">{isLoading ? <Loader2Icon/> : 'Aktivieren & Bestätigen'}</button>
                    </div>
                </form>
            );
        }

        if (twoFactorView === 'disable') {
             return (
                <form onSubmit={handleDisable2FA} className="space-y-4 bg-red-500/10 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-red-700 dark:text-red-300">Geben Sie einen aktuellen Code aus Ihrer Authenticator-App ein, um 2FA zu deaktivieren.</p>
                    <div>
                        <label htmlFor="disableCode" className="text-sm font-medium text-gray-600 dark:text-gray-300">Aktueller 2FA-Code</label>
                        <input type="text" name="disableCode" id="disableCode" required autoFocus value={verificationCode} onChange={e => setVerificationCode(e.target.value)} className="input-field" inputMode="numeric" pattern="\d{6}" maxLength={6} />
                    </div>
                    <div className="flex justify-end gap-4">
                        <button type="button" onClick={() => setTwoFactorView('idle')} disabled={isLoading} className="btn-secondary">Abbrechen</button>
                        <button type="submit" disabled={isLoading} className="btn-danger w-48">{isLoading ? <Loader2Icon/> : '2FA Deaktivieren'}</button>
                    </div>
                </form>
            );
        }

        return (
            <div className={`p-4 rounded-lg flex items-center justify-between ${is2FAEnabled ? 'bg-green-500/10' : 'bg-gray-500/10'}`}>
                <div>
                    <p className={`font-semibold ${is2FAEnabled ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>Status</p>
                    <p className={`text-sm ${is2FAEnabled ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>{is2FAEnabled ? 'Aktiviert' : 'Deaktiviert'}</p>
                </div>
                {is2FAEnabled ? (
                    <button onClick={() => setTwoFactorView('disable')} className="btn-danger w-48">2FA Deaktivieren</button>
                ) : (
                    <button onClick={handleStart2FASetup} disabled={isLoading} className="btn-primary w-48 flex items-center justify-center gap-2"><ShieldCheckIcon/> 2FA Aktivieren</button>
                )}
            </div>
        );
    };

    return (
        <Modal title="Kontoeinstellungen" onClose={onClose}>
            <div className="flex flex-col h-[80vh]">
                {/* Tab Navigation */}
                <div role="tablist" aria-label="Kontoeinstellungen Tabs" className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-6 px-1 shrink-0 overflow-x-auto scrollbar-hide">
                    {[
                        { id: 'profile', label: 'Profil', icon: <UserIcon className="h-4 w-4" /> },
                        { id: 'security', label: 'Sicherheit', icon: <ShieldCheckIcon className="h-4 w-4" /> },
                        { id: 'gemini', label: 'KI-Anbieter', icon: <ChipIcon className="h-4 w-4" /> },
                        { id: 'workspace', label: 'Arbeitsbereich', icon: <SlidersIcon className="h-4 w-4" /> }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            role="tab"
                            aria-selected={activeTab === tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all relative ${
                                activeTab === tab.id 
                                ? 'text-indigo-600 dark:text-indigo-400' 
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                            }`}
                        >
                            {tab.icon}
                            {tab.label}
                            {activeTab === tab.id && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
                            )}
                        </button>
                    ))}
                </div>

                <div className="flex-grow overflow-y-auto pr-2 space-y-8 animate-view-transition">
                    {activeTab === 'profile' && (
                        <section className="animate-slide-up">
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Angemeldet als:</p>
                            <p className="font-semibold text-gray-800 dark:text-gray-200 text-lg mb-6">{user.email}</p>
                            
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">Passwort ändern</h3>
                            <form onSubmit={handlePasswordChange} className="space-y-4">
                                <div><label htmlFor="currentPassword">Aktuelles Passwort</label><input type="password" id="currentPassword" required value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="input-field" /></div>
                                <div><label htmlFor="newPassword">Neues Passwort</label><input type="password" id="newPassword" required value={newPassword} onChange={e => setNewPassword(e.target.value)} className="input-field" /></div>
                                <div><label htmlFor="confirmNewPassword">Neues Passwort bestätigen</label><input type="password" id="confirmNewPassword" required value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} className="input-field" /></div>
                                <div className="flex justify-end"><button type="submit" disabled={isLoading} className="btn-primary w-48">{isLoading ? <Loader2Icon/> : 'Passwort speichern'}</button></div>
                            </form>
                        </section>
                    )}

                    {activeTab === 'security' && (
                        <section className="animate-slide-up">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center gap-2">Zwei-Faktor-Authentifizierung</h3>
                            {render2FAContent()}

                            <div className="mt-12">
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">Zugangsschlüssel für Skill-Sync</h3>
                                <SyncTokens />
                            </div>
                            
                            <div className="mt-12">
                                <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4 border-b border-red-500/30 pb-2">Konto löschen</h3>
                                {!isDeleteConfirmVisible ? (
                                    <><p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Wenn Sie Ihr Konto löschen, werden alle Ihre Daten dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.</p><div className="flex justify-end"><button onClick={() => setIsDeleteConfirmVisible(true)} className="btn-danger w-48"><TrashIcon /> Konto löschen</button></div></>
                                ) : (
                                    <form onSubmit={handleDeleteAccountSubmit} className="space-y-4 bg-red-500/10 p-4 rounded-lg">
                                        <p className="text-sm font-semibold text-red-700 dark:text-red-300">Geben Sie Ihr Passwort ein, um die Löschung zu bestätigen.</p>
                                        <div><label htmlFor="deletePassword">Ihr Passwort</label><input type="password" id="deletePassword" required autoFocus value={deletePassword} onChange={e => setDeletePassword(e.target.value)} className="input-field" /></div>
                                        <div className="flex justify-end gap-4"><button type="button" onClick={() => setIsDeleteConfirmVisible(false)} disabled={isLoading} className="btn-secondary">Abbrechen</button><button type="submit" disabled={isLoading} className="btn-danger w-60">{isLoading ? <Loader2Icon/> : 'Mein Konto endgültig löschen'}</button></div>
                                    </form>
                                )}
                            </div>
                        </section>
                    )}

                    {activeTab === 'gemini' && (
                        <section className="animate-slide-up">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">Google Gemini API Keys</h3>
                            <form onSubmit={handleSaveGeminiApiKey} className="space-y-4 bg-sheet p-5 rounded-2xl border border-blue-500/20 shadow-sm">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                                        <BrainCircuitIcon /> API Key hinzufügen
                                    </h4>
                                    {hasGeminiKey && (
                                        <div className="flex items-center gap-3">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <div className="relative">
                                                    <input type="checkbox" className="sr-only" checked={enableGeminiKeyRotation} onChange={e => {
                                                        const newValue = e.target.checked;
                                                        setEnableGeminiKeyRotation(newValue);
                                                        onUpdateSettings({
                                                            globalSystemInstruction: globalInstruction,
                                                            promptGeneratorSystemPrompt: generatorSystemPrompt,
                                                            promptOptimizerSystemPrompt: optimizerSystemPrompt,
                                                            selectedProviderId,
                                                            testModel,
                                                            generatorModel,
                                                            optimizerModel,
                                                            customProviders,
                                                            enableGeminiKeyRotation: newValue
                                                        });
                                                        showToast('Rotation-Einstellung gespeichert!');
                                                    }} />
                                                    <div className={`block w-10 h-6 rounded-full transition-colors ${enableGeminiKeyRotation ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                                                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${enableGeminiKeyRotation ? 'transform translate-x-4' : ''}`}></div>
                                                </div>
                                                <span className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">Rotation</span>
                                            </label>
                                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-bold uppercase tracking-wider border border-blue-500/20">
                                                {geminiKeys.length} Keys
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                                    {hasGeminiKey
                                        ? `Deine Schlüssel werden automatisch rotiert, um Rate-Limits zu umgehen. Ideal für die Nutzung kostenloser Tiers.`
                                        : 'Hinterlege mindestens einen persönlichen Gemini API-Schlüssel. Bei mehreren Schlüsseln erfolgt eine automatische Rotation.'}
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="geminiApiKeyLabel" className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase ml-1">Bezeichnung</label>
                                        <input type="text" id="geminiApiKeyLabel" value={geminiKeyLabelInput} onChange={e => setGeminiKeyLabelInput(e.target.value)} className="input-field mt-1" placeholder="z. B. Privat / Business" />
                                    </div>
                                    <div>
                                        <label htmlFor="geminiApiKey" className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase ml-1">API-Schlüssel</label>
                                        <input type="password" id="geminiApiKey" value={geminiKeyInput} onChange={e => setGeminiKeyInput(e.target.value)} className="input-field mt-1" placeholder="AIza..." />
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-3 justify-end pt-2">
                                    <button type="button" onClick={handleTestGeminiApiKey} className="btn-secondary px-6" disabled={isTestingGeminiKey || isSavingGeminiKey}>
                                        {isTestingGeminiKey ? <Loader2Icon className="animate-spin" /> : <ShieldCheckIcon />}
                                        <span className="ml-2">Verbindung testen</span>
                                    </button>
                                    <button type="submit" className="btn-primary px-8" disabled={isSavingGeminiKey || isTestingGeminiKey}>
                                        {isSavingGeminiKey ? <Loader2Icon className="animate-spin" /> : 'Schlüssel speichern'}
                                    </button>
                                </div>
                            </form>

                            {geminiKeys.length > 0 && (
                                <div className="mt-8 space-y-3">
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Gespeicherte Schlüssel:</p>
                                    <ul className="space-y-2">
                                        {geminiKeys.map((entry) => {
                                            const successRate = entry.totalRequests ? Math.round(((entry.totalRequests - (entry.failedRequests || 0)) / entry.totalRequests) * 100) : 100;
                                            const healthColor = successRate > 90 ? 'text-green-500' : successRate > 50 ? 'text-amber-500' : 'text-red-500';
                                            
                                            return (
                                                <li key={entry.id} className="flex items-center justify-between rounded-xl border border-blue-500/10 bg-white/60 dark:bg-gray-800/60 px-4 py-3 group hover:border-blue-500/40 transition-all shadow-sm">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-0.5">
                                                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{entry.label}</p>
                                                            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-[9px] font-bold text-gray-500 dark:text-gray-400">
                                                                ID: {entry.id}
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                                            <p className="text-[10px] text-gray-500 flex items-center gap-1">
                                                                <HistoryIcon className="w-3 h-3" />
                                                                {entry.lastUsedAt ? new Date(entry.lastUsedAt).toLocaleString() : 'Noch nie genutzt'}
                                                            </p>
                                                            <p className="text-[10px] text-gray-500 flex items-center gap-1">
                                                                <SparklesIcon className="w-3 h-3" />
                                                                {entry.totalRequests || 0} Anfragen
                                                            </p>
                                                            <p className={`text-[10px] font-bold flex items-center gap-1 ${healthColor}`}>
                                                                <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                                                                {successRate}% Erfolg
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                                                        <button 
                                                            type="button" 
                                                            onClick={() => handleRemoveGeminiApiKey(entry.id)} 
                                                            className="text-gray-400 hover:text-red-500 p-2 transition-colors focus:opacity-100"
                                                            title="Schlüssel löschen"
                                                            aria-label="Schlüssel löschen"
                                                        >
                                                            <TrashIcon />
                                                        </button>
                                                    </div>
                                                </li>
                                            );
                                        })}

                                    </ul>
                                </div>
                            )}

                            <div className="mt-12">
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">Benutzerdefinierte Gemini Modelle</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Füge eigene oder neue Gemini Modelle hinzu (z.B. experimentelle Versionen), die noch nicht in der Standardliste aufgeführt sind.</p>
                                
                                <div className="space-y-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Modell ID</label>
                                            <input type="text" value={newCustomGeminiModel} onChange={e => setNewCustomGeminiModel(e.target.value)} className="input-field" placeholder="z. B. gemini-3-flash-preview" />
                                        </div>
                                    </div>
                                    <div className="flex justify-end">
                                        <button type="button" onClick={handleAddCustomGeminiModel} className="btn-secondary">Modell hinzufügen</button>
                                    </div>
                                </div>

                                {customGeminiModels.length > 0 && (
                                    <div className="mt-6 space-y-3">
                                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Eigene Modelle:</p>
                                        <ul className="space-y-2">
                                            {customGeminiModels.map((model) => (
                                                <li key={model} className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{model}</p>
                                                    </div>
                                                    <button type="button" onClick={() => handleRemoveCustomGeminiModel(model)} className="text-red-500 hover:text-red-700 p-2"><TrashIcon /></button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <div className="mt-12">
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">Benutzerdefinierte API Provider (OpenAI kompatibel)</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Füge eigene Provider hinzu (z. B. lokales Ollama, LM Studio oder OpenAI). Du kannst die Provider und spezifische Modelle im Tab "Workspace" den jeweiligen Funktionen zuweisen.</p>
                                
                                <div className="space-y-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Name des Providers</label>
                                            <input type="text" value={newProviderName} onChange={e => setNewProviderName(e.target.value)} className="input-field" placeholder="z. B. Mein lokales Ollama" />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Base URL</label>
                                            <input type="text" value={newProviderUrl} onChange={e => setNewProviderUrl(e.target.value)} className="input-field" placeholder="z. B. http://localhost:11434/v1" />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600 dark:text-gray-300">API Key (optional falls lokal)</label>
                                            <input type="password" value={newProviderKey} onChange={e => setNewProviderKey(e.target.value)} className="input-field" placeholder="sk-..." />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Standard-Modell</label>
                                            <input type="text" value={newProviderModel} onChange={e => setNewProviderModel(e.target.value)} className="input-field" placeholder="z. B. llama3" />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Verfügbare Modelle (kommagetrennt)</label>
                                            <input type="text" value={newProviderAvailableModels} onChange={e => setNewProviderAvailableModels(e.target.value)} className="input-field" placeholder="z. B. llama3, gemma, mistral" />
                                        </div>
                                    </div>
                                    <div className="flex justify-end">
                                        <button type="button" onClick={handleAddCustomProvider} className="btn-secondary">Provider hinzufügen</button>
                                    </div>
                                </div>

                                {customProviders.length > 0 && (
                                    <div className="mt-6 space-y-3">
                                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Meine Provider:</p>
                                        <ul className="space-y-2">
                                            {customProviders.map((provider) => (
                                                <li key={provider.id} className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{provider.name}</p>
                                                        <p className="text-xs text-gray-500">URL: {provider.baseUrl} | Modell: {provider.defaultModel}</p>
                                                    </div>
                                                    <button type="button" onClick={() => handleRemoveCustomProvider(provider.id)} className="text-red-500 hover:text-red-700 p-2"><TrashIcon /></button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {activeTab === 'workspace' && (
                        <section className="animate-slide-up space-y-6">
                            <div className="flex items-center gap-3 mb-2 text-amber-500">
                                <SlidersIcon />
                                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">KI-Funktionen & Feineinstellungen</h3>
                            </div>
                            
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Hier kannst du das Verhalten der verschiedenen KI-Module individuell anpassen. Jede Funktion arbeitet mit ihrem eigenen System-Prompt unabhängig von den anderen.
                            </p>

                            <form onSubmit={handleSaveWorkspaceSettings} className="space-y-6">
                                <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
                                    <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-3">Globaler KI Provider</h4>
                                    <select
                                        value={selectedProviderId}
                                        onChange={e => {
                                            const newProviderId = e.target.value;
                                            setSelectedProviderId(newProviderId);
                                            if (newProviderId === 'gemini') {
                                                setTestModel('');
                                                setGeneratorModel('');
                                                setOptimizerModel('');
                                            } else {
                                                const p = customProviders.find(cp => cp.id === newProviderId);
                                                const defaultM = p?.defaultModel || p?.availableModels?.[0] || '';
                                                setTestModel(defaultM);
                                                setGeneratorModel(defaultM);
                                                setOptimizerModel(defaultM);
                                            }
                                        }}
                                        className="input-field max-w-sm mb-2"
                                    >
                                        <option value="gemini">Google Gemini (Standard)</option>
                                        {customProviders.map(p => (
                                            <option key={p.id} value={p.id}>{p.name} (Custom)</option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500">Wähle den primären Anbieter aus. Du kannst spezifische Modelle für jede Funktion definieren.</p>
                                </div>

                                {/* Card 1: Global context (Test Prompt) */}
                                <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">
                                                <BrainCircuitIcon />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-800 dark:text-gray-100">Globaler Kontext (Test-Funktion)</h4>
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400">Gilt nur für "Prompt testen" im Editor. Leer = Standard.</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setGlobalInstruction(DEFAULT_TEST_SYSTEM_PROMPT)}
                                            className="text-[10px] flex items-center gap-1 text-indigo-500 hover:text-indigo-600 font-semibold uppercase tracking-wider"
                                            title="Standard zum Anpassen einfügen"
                                        >
                                            <HistoryIcon /> Standard
                                        </button>
                                    </div>
                                    <textarea
                                        id="globalInstruction"
                                        rows={3}
                                        value={globalInstruction}
                                        onChange={e => setGlobalInstruction(e.target.value)}
                                        className="input-field min-h-[80px] text-sm mb-4"
                                        placeholder="Leer lassen für den Standard: Prompt unverändert ausführen, Platzhalter mit Beispielen füllen, auf Deutsch antworten."
                                    />
                                    
                                    <ModelSelector
                                        label="Modell für Test‑Funktion"
                                        value={testModel}
                                        onChange={setTestModel}
                                        providerName={selectedProviderId === 'gemini' ? 'Gemini' : (customProviders.find(p => p.id === selectedProviderId)?.name || 'Custom')}
                                        options={(() => {
                                            if (selectedProviderId === 'gemini') return [...GEMINI_MODELS, ...customGeminiModels.map(m => ({ id: m, label: m, description: 'Benutzerdefiniertes Modell' }))];
                                            const provider = customProviders.find(p => p.id === selectedProviderId);
                                            const models = provider?.availableModels?.length ? provider?.availableModels : [provider?.defaultModel ?? ''];
                                            return models.filter(m => m).map(m => ({ id: m, label: m }));
                                        })()}
                                    />
                                </div>

                                {/* Card 2: Prompt Generator (Chat) */}
                                <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <div className="p-2 rounded-lg bg-purple-500/10 text-purple-600 dark:purple-400">
                                                <SparklesIcon />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-800 dark:text-gray-100">Prompt-Generator (Chat)</h4>
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400">Definiert die iterative Arbeitsweise des Assistenten.</p>
                                            </div>
                                        </div>
                                        <button 
                                            type="button" 
                                            onClick={() => setGeneratorSystemPrompt(DEFAULT_GENERATOR_PROMPT)}
                                            className="text-[10px] flex items-center gap-1 text-indigo-500 hover:text-indigo-600 font-semibold uppercase tracking-wider"
                                            title="Standard wiederherstellen"
                                        >
                                            <HistoryIcon /> Standard
                                        </button>
                                    </div>
                                    <textarea
                                        id="generatorSystemPrompt"
                                        rows={4}
                                        value={generatorSystemPrompt}
                                        onChange={e => setGeneratorSystemPrompt(e.target.value)}
                                        className="input-field min-h-[120px] text-sm font-mono leading-relaxed mb-4"
                                        placeholder="Leer lassen für den Standard (Klick auf „Standard“ zeigt ihn zum Anpassen)."
                                    />
                                    
                                    <ModelSelector
                                        label="Modell für Prompt‑Generator"
                                        value={generatorModel}
                                        onChange={setGeneratorModel}
                                        providerName={selectedProviderId === 'gemini' ? 'Gemini' : (customProviders.find(p => p.id === selectedProviderId)?.name || 'Custom')}
                                        options={(() => {
                                            if (selectedProviderId === 'gemini') return [...GEMINI_MODELS, ...customGeminiModels.map(m => ({ id: m, label: m, description: 'Benutzerdefiniertes Modell' }))];
                                            const provider = customProviders.find(p => p.id === selectedProviderId);
                                            const models = provider?.availableModels?.length ? provider?.availableModels : [provider?.defaultModel ?? ''];
                                            return models.filter(m => m).map(m => ({ id: m, label: m }));
                                        })()}
                                    />
                                </div>

                                {/* Card 3: Prompt Optimizer */}
                                <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                                <BrainCircuitIcon />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-800 dark:text-gray-100">Prompt-Optimierung</h4>
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400">Gibt vor, wie bestehende Prompts verbessert werden sollen.</p>
                                            </div>
                                        </div>
                                        <button 
                                            type="button" 
                                            onClick={() => setOptimizerSystemPrompt(DEFAULT_OPTIMIZER_PROMPT)}
                                            className="text-[10px] flex items-center gap-1 text-indigo-500 hover:text-indigo-600 font-semibold uppercase tracking-wider"
                                            title="Standard wiederherstellen"
                                        >
                                            <HistoryIcon /> Standard
                                        </button>
                                    </div>
                                    <textarea
                                        id="optimizerSystemPrompt"
                                        rows={4}
                                        value={optimizerSystemPrompt}
                                        onChange={e => setOptimizerSystemPrompt(e.target.value)}
                                        className="input-field min-h-[120px] text-sm font-mono leading-relaxed mb-4"
                                        placeholder="Leer lassen für den Standard (Klick auf „Standard“ zeigt ihn zum Anpassen)."
                                    />
                                    
                                    <ModelSelector
                                        label="Modell für Prompt‑Optimierung"
                                        value={optimizerModel}
                                        onChange={setOptimizerModel}
                                        providerName={selectedProviderId === 'gemini' ? 'Gemini' : (customProviders.find(p => p.id === selectedProviderId)?.name || 'Custom')}
                                        options={(() => {
                                            if (selectedProviderId === 'gemini') return [...GEMINI_MODELS, ...customGeminiModels.map(m => ({ id: m, label: m, description: 'Benutzerdefiniertes Modell' }))];
                                            const provider = customProviders.find(p => p.id === selectedProviderId);
                                            const models = provider?.availableModels?.length ? provider?.availableModels : [provider?.defaultModel ?? ''];
                                            return models.filter(m => m).map(m => ({ id: m, label: m }));
                                        })()}
                                    />
                                </div>

                                <div className="flex justify-end pt-4">
                                    <button type="submit" className="btn-primary px-12 py-3 shadow-lg shadow-indigo-500/20" disabled={isSavingSettings}>
                                        {isSavingSettings ? <Loader2Icon /> : 'Alle Einstellungen speichern'}
                                    </button>
                                </div>
                            </form>
                        </section>
                    )}
                </div>
            </div>
            <style>{`
                .input-field { margin-top: 0.25rem; width: 100%; padding: 0.5rem 0.75rem; border-radius: 0.375rem; background-color: #f3f4f6; border: 1px solid #d1d5db; color: #111827; }
                .dark .input-field { background-color: #374151; border-color: #4b5563; color: #f9fafb; }
                .input-field:focus { outline: none; --tw-ring-color: #3b82f6; box-shadow: 0 0 0 2px var(--tw-ring-color); border-color: #3b82f6; }
                #confirmNewPassword, #newPassword, #currentPassword, #deletePassword { font-family: text-security-disc,-apple-system,BlinkMacSystemFont,sans-serif; -webkit-text-security: disc; }
                label { font-size: 0.875rem; font-weight: 500; color: #4b5563; }
                .dark label { color: #d1d5db; }
                .btn-primary, .btn-danger, .btn-secondary { display: flex; justify-content: center; align-items: center; padding: 0.5rem 1rem; border-radius: 0.375rem; font-weight: 600; transition: background-color 0.2s; }
                .btn-primary { color: white; background-color: #2563eb; } .btn-primary:hover { background-color: #1d4ed8; } .btn-primary:disabled { background-color: #93c5fd; cursor: not-allowed; }
                .btn-danger { gap: 0.5rem; color: white; background-color: #dc2626; } .btn-danger:hover { background-color: #b91c1c; }
                .btn-secondary { color: #1f2937; background-color: #e5e7eb; } .dark .btn-secondary { color: #f9fafb; background-color: #4b5563; }
                .btn-secondary:hover { background-color: #d1d5db; } .dark .btn-secondary:hover { background-color: #6b7280; }
            `}</style>
        </Modal>
    );
};

export default AccountSettingsModal;
